/**
 * IndexedDB-based offline storage for caching job listings and user data
 * Provides persistent storage that survives browser restarts
 */

const DB_NAME = 'BoholTrabahoOffline';
const DB_VERSION = 2; // v2: applications store (per-job applicants-table cache)

// Store names
const STORES = {
  JOBS: 'jobs',
  CATEGORIES: 'categories',
  PROFILE: 'profile',
  PENDING_ACTIONS: 'pendingActions',
  APPLICATIONS: 'applications'
};

let db = null;

/**
 * Initialize IndexedDB
 */
export async function initOfflineDB() {
  if (db) return db;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[OfflineDB] Failed to open database:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('[OfflineDB] Database initialized');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Jobs store with indexes
      if (!database.objectStoreNames.contains(STORES.JOBS)) {
        const jobsStore = database.createObjectStore(STORES.JOBS, { keyPath: 'id' });
        jobsStore.createIndex('category_id', 'category_id', { unique: false });
        jobsStore.createIndex('location', 'location', { unique: false });
        jobsStore.createIndex('post_type', 'post_type', { unique: false });
        jobsStore.createIndex('created_at', 'created_at', { unique: false });
      }

      // Categories store
      if (!database.objectStoreNames.contains(STORES.CATEGORIES)) {
        database.createObjectStore(STORES.CATEGORIES, { keyPath: 'id' });
      }

      // Profile store (single user profile)
      if (!database.objectStoreNames.contains(STORES.PROFILE)) {
        database.createObjectStore(STORES.PROFILE, { keyPath: 'id' });
      }

      // Applicants-table cache: one record per job (stale-while-revalidate paint)
      if (!database.objectStoreNames.contains(STORES.APPLICATIONS)) {
        database.createObjectStore(STORES.APPLICATIONS, { keyPath: 'job_id' });
      }

      // Pending actions store (for background sync)
      if (!database.objectStoreNames.contains(STORES.PENDING_ACTIONS)) {
        const actionsStore = database.createObjectStore(STORES.PENDING_ACTIONS, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        actionsStore.createIndex('timestamp', 'timestamp', { unique: false });
        actionsStore.createIndex('type', 'type', { unique: false });
      }

      console.log('[OfflineDB] Database schema created');
    };
  });
}

/**
 * Get database instance
 */
async function getDB() {
  if (!db) {
    await initOfflineDB();
  }
  return db;
}

/**
 * Save jobs to IndexedDB
 */
export async function saveJobsToCache(jobs) {
  try {
    const database = await getDB();
    const transaction = database.transaction(STORES.JOBS, 'readwrite');
    const store = transaction.objectStore(STORES.JOBS);

    // Add cache timestamp to each job
    const cachedAt = new Date().toISOString();
    
    for (const job of jobs) {
      store.put({ ...job, _cachedAt: cachedAt });
    }

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        console.log(`[OfflineDB] Cached ${jobs.length} jobs`);
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('[OfflineDB] Failed to cache jobs:', error);
  }
}

/**
 * Get jobs from IndexedDB cache
 */
export async function getJobsFromCache(filters = {}) {
  try {
    const database = await getDB();
    const transaction = database.transaction(STORES.JOBS, 'readonly');
    const store = transaction.objectStore(STORES.JOBS);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      
      request.onsuccess = () => {
        let jobs = request.result || [];

        // Apply filters
        if (filters.location && filters.location !== 'all') {
          jobs = jobs.filter(job =>
            (job.locations && job.locations.includes(filters.location)) ||
            job.location === filters.location
          );
        }
        if (filters.post_type && filters.post_type !== 'all') {
          jobs = jobs.filter(job => job.post_type === filters.post_type);
        }
        if (filters.category_id && filters.category_id !== 'all') {
          jobs = jobs.filter(job => job.category_id === filters.category_id);
        }

        // Sort by created_at descending
        jobs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        console.log(`[OfflineDB] Retrieved ${jobs.length} cached jobs`);
        resolve(jobs);
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[OfflineDB] Failed to get cached jobs:', error);
    return [];
  }
}

/**
 * Save categories to IndexedDB
 */
export async function saveCategoriesToCache(categories) {
  try {
    const database = await getDB();
    const transaction = database.transaction(STORES.CATEGORIES, 'readwrite');
    const store = transaction.objectStore(STORES.CATEGORIES);

    for (const category of categories) {
      store.put(category);
    }

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        console.log(`[OfflineDB] Cached ${categories.length} categories`);
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('[OfflineDB] Failed to cache categories:', error);
  }
}

/**
 * Get categories from IndexedDB cache
 */
export async function getCategoriesFromCache() {
  try {
    const database = await getDB();
    const transaction = database.transaction(STORES.CATEGORIES, 'readonly');
    const store = transaction.objectStore(STORES.CATEGORIES);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      
      request.onsuccess = () => {
        console.log(`[OfflineDB] Retrieved ${request.result?.length || 0} cached categories`);
        resolve(request.result || []);
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[OfflineDB] Failed to get cached categories:', error);
    return [];
  }
}

/**
 * Save a pending action for later sync
 */
/**
 * Cache a job's applicants list (stale-while-revalidate for the applicants table).
 * One record per job: { job_id, applications, cachedAt }.
 */
export async function saveApplicationsToCache(jobId, applications) {
  try {
    const database = await initOfflineDB();
    const transaction = database.transaction(STORES.APPLICATIONS, 'readwrite');
    const store = transaction.objectStore(STORES.APPLICATIONS);
    store.put({ job_id: jobId, applications, cachedAt: Date.now() });
    return new Promise((resolve) => {
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => resolve(false);
    });
  } catch (error) {
    console.warn('[OfflineDB] Failed to cache applications:', error);
    return false;
  }
}

/**
 * Cached applicants for a job, or null when absent/expired. Default max age 30 min —
 * cached document links carry 1-hour signed URLs, so an expired cache must never paint.
 */
export async function getApplicationsFromCache(jobId, maxAgeMs = 30 * 60 * 1000) {
  try {
    const database = await initOfflineDB();
    const transaction = database.transaction(STORES.APPLICATIONS, 'readonly');
    const store = transaction.objectStore(STORES.APPLICATIONS);
    return new Promise((resolve) => {
      const request = store.get(jobId);
      request.onsuccess = () => {
        const record = request.result;
        if (record && Array.isArray(record.applications) && (Date.now() - (record.cachedAt || 0)) < maxAgeMs) {
          resolve(record.applications);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => resolve(null);
    });
  } catch (error) {
    console.warn('[OfflineDB] Failed to read cached applications:', error);
    return null;
  }
}

export async function savePendingAction(action) {
  try {
    const database = await getDB();
    const transaction = database.transaction(STORES.PENDING_ACTIONS, 'readwrite');
    const store = transaction.objectStore(STORES.PENDING_ACTIONS);

    const pendingAction = {
      ...action,
      timestamp: new Date().toISOString(),
      retryCount: 0
    };

    return new Promise((resolve, reject) => {
      const request = store.add(pendingAction);
      request.onsuccess = () => {
        console.log('[OfflineDB] Saved pending action:', action.type);
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[OfflineDB] Failed to save pending action:', error);
  }
}

/**
 * Get all pending actions
 */
export async function getPendingActions() {
  try {
    const database = await getDB();
    const transaction = database.transaction(STORES.PENDING_ACTIONS, 'readonly');
    const store = transaction.objectStore(STORES.PENDING_ACTIONS);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[OfflineDB] Failed to get pending actions:', error);
    return [];
  }
}

/**
 * Remove a pending action after successful sync
 */
export async function removePendingAction(id) {
  try {
    const database = await getDB();
    const transaction = database.transaction(STORES.PENDING_ACTIONS, 'readwrite');
    const store = transaction.objectStore(STORES.PENDING_ACTIONS);

    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => {
        console.log('[OfflineDB] Removed pending action:', id);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[OfflineDB] Failed to remove pending action:', error);
  }
}

/**
 * Clear all cached data
 */
export async function clearAllCache() {
  try {
    const database = await getDB();
    const storeNames = [STORES.JOBS, STORES.CATEGORIES];
    
    for (const storeName of storeNames) {
      const transaction = database.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      store.clear();
    }
    
    console.log('[OfflineDB] All cache cleared');
  } catch (error) {
    console.error('[OfflineDB] Failed to clear cache:', error);
  }
}

/**
 * Get cache statistics
 */
export async function getCacheStats() {
  try {
    const database = await getDB();
    const stats = {};

    for (const storeName of Object.values(STORES)) {
      const transaction = database.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      
      stats[storeName] = await new Promise((resolve) => {
        const countRequest = store.count();
        countRequest.onsuccess = () => resolve(countRequest.result);
        countRequest.onerror = () => resolve(0);
      });
    }

    return stats;
  } catch (error) {
    console.error('[OfflineDB] Failed to get cache stats:', error);
    return {};
  }
}

// Initialize on module load
if (typeof window !== 'undefined' && 'indexedDB' in window) {
  initOfflineDB().catch(console.error);
}
